'use client'

// His wall, hung where Sam put it.
//
// The planes live in tag_planes.glb, one node per wall named tag_01…
// tag_06, and the sim owns what is on them: it keeps a PNG per wall and
// paints each new tag on top of the last, so this side never composites
// anything. It asks which walls have paint on them, hangs the image, and
// re-hangs it when the sim says it changed.
//
// A plain PNG rather than KTX2 on purpose, and it is the one texture in
// the world that has to be: the wall CHANGES, and a compressed texture
// would mean transcoding a fresh file every time he sprays. It is one
// 1024x512 image per wall, loaded only when a wall actually has something
// on it, so the cost is a wall he has painted, never a wall he has not.
//
// Unlit on purpose. The tags are emulsion on concrete and the plane sits
// a few centimetres proud of the building, so lighting it twice (once on
// the building, once here) reads as a glowing sticker.
//
// The walls are different shapes (1.16:1 up to a 20 metre 3.8:1), so the
// sim renders each one's canvas at its own aspect, measured off this very
// GLB. Nothing here rescales: the image already fits the plane.

import { Component, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three/webgpu'
import { useModel } from '../hooks/useModel'
import { ditherMaterial } from '../effects/ditherRegistry'

// Same conversion the wallet and the price panel do: the world is reached
// over the socket, its files over http on the same host.
function simBase() {
  const ws = process.env.NEXT_PUBLIC_SIM_WS_URL || ''
  return ws.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://').replace(/\/$/, '')
}

const MODEL = '/models/tag_planes.glb'
const POLL_MS = 60_000
const NODE = /^tag_\d{2}$/

// A missing model must cost the WALL, never the world.
//
// Shipped 23 Aug with the loader mounted and the .glb still untracked, so
// production 404'd and the whole town went down behind the crash guard at
// 2s uptime. useModel suspends and then THROWS, and a throw inside the
// canvas takes everything with it.
//
// So the wall gets its own boundary. Anything else added here should get
// one too: dressing is dressing, and no piece of it is worth the world.
class Absent extends Component {
  state = { gone: false }
  static getDerivedStateFromError() { return { gone: true } }
  componentDidCatch(err) {
    console.warn('[spraywalls] no walls this session:', err?.message || err)
  }
  render() { return this.state.gone ? null : this.props.children }
}

export function SprayWalls() {
  return (
    <Absent>
      <Suspense fallback={null}>
        <Walls />
      </Suspense>
    </Absent>
  )
}

function Walls() {
  const { scene } = useModel(MODEL)
  const [walls, setWalls] = useState([])
  const loaded = useRef(new Map())   // id -> updatedAt currently hanging

  // Which walls have paint, and when each last changed. Cheap: the index
  // is a few numbers, and the images are only fetched when the number
  // moves.
  useEffect(() => {
    let dead = false
    const look = () => fetch(`${simBase()}/api/walls`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { if (!dead && Array.isArray(d?.walls)) setWalls(d.walls) })
      .catch(() => { /* a wall nobody can reach is just a wall */ })
    look()
    const t = setInterval(look, POLL_MS)
    return () => { dead = true; clearInterval(t) }
  }, [])

  // The planes, found by name once. Unlit and double-sided-off: these are
  // flat faces on buildings and you only ever see the front.
  const faces = useMemo(() => {
    const out = new Map()
    scene.traverse((o) => {
      if (!o.isMesh || !NODE.test(o.name)) return
      // Transparent, because the wall image is ONLY his paint: the
      // building's own brick shows through everywhere he has not
      // sprayed. depthWrite off so a soft-edged tag never punches a hole
      // in whatever is drawn behind it.
      //
      // LIT, not unlit. An unlit material ignores the time of day, so at
      // night the wall stayed at full daylight brightness and every tag
      // read as neon while the brick behind it went dark blue. Paint is
      // paint: it takes the same light the wall takes.
      const mat = new THREE.MeshStandardNodeMaterial({ transparent: true })
      mat.roughness = 0.94
      mat.metalness = 0
      mat.depthWrite = false
      // Dithered from the first frame it draws, not from the next sweep.
      ditherMaterial(mat)
      o.material = mat
      // A fresh tag means a fresh texture on this material, and the
      // Precompiler rebuilds its pipeline off the frame when that lands.
      o.userData.precompile = true
      // Nothing on it yet: a bare plane would be a grey rectangle stuck
      // to a building, which is worse than no wall at all.
      o.visible = false
      o.castShadow = false
      o.receiveShadow = false
      out.set(o.name, o)
    })
    return out
  }, [scene])

  // Hang what he has sprayed, and re-hang it when it changes.
  useEffect(() => {
    let dead = false
    const loader = new THREE.TextureLoader()
    for (const w of walls) {
      const mesh = faces.get(w.id)
      if (!mesh) continue                       // a wall the sim knows and the model does not
      if (loaded.current.get(w.id) === w.updatedAt) continue
      // The version in the query is what busts the cache: same filename
      // forever, different paint.
      loader.load(
        `${simBase()}/api/walls/${w.id}.png?v=${w.updatedAt}`,
        (tex) => {
          if (dead) return
          tex.colorSpace = THREE.SRGBColorSpace
          // The pixel look up front: the sweep forces nearest filtering a
          // few seconds after a fresh tag hangs, and the smooth-then-snap
          // in between reads as the wall re-rendering.
          tex.magFilter = THREE.NearestFilter
          tex.minFilter = THREE.NearestFilter
          // glTF UVs are top-down and a loose texture is not, which is the
          // same trap the mushroom atlas hit: leave flipY true here and
          // every tag hangs upside down on the wall.
          tex.flipY = false
          const old = mesh.material.map
          mesh.material.map = tex
          mesh.material.needsUpdate = true
          mesh.visible = true
          loaded.current.set(w.id, w.updatedAt)
          // The wall we just replaced is ours and nobody else's.
          if (old && old !== tex) old.dispose()
        },
        undefined,
        () => { /* leave whatever is already hanging */ },
      )
    }
    return () => { dead = true }
  }, [walls, faces])

  // Textures are this component's own, so they go when it does.
  useEffect(() => () => {
    for (const mesh of faces.values()) {
      mesh.material?.map?.dispose?.()
      mesh.material?.dispose?.()
    }
  }, [faces])

  return <primitive object={scene} dispose={null} />
}

export default SprayWalls
